import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { 
  Brain, 
  User, 
  Briefcase, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  MapPin,
  DollarSign,
  Star,
  ChevronDown,
  ChevronUp,
  Loader2
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ScreeningAnalysis {
  candidate_summary: {
    name: string;
    experience_level: string;
    primary_skills: string[];
    years_experience: string;
    current_role: string;
    salary_expectations: string;
    availability: string;
    location_preference: string;
  };
  screening_assessment: {
    communication_quality: string;
    technical_competency: string;
    cultural_fit_indicators: string[];
    red_flags: string[];
    green_flags: string[];
    overall_impression: string;
  };
  job_matches: Array<{
    job_title: string;
    match_score: number;
    match_rationale: string;
    skill_alignment: string[];
    concerns: string[];
    recommendation: string;
  }>;
  next_steps: {
    recommended_actions: string[];
    interview_focus_areas: string[];
    additional_screening_needed: string;
  };
}

interface AIScreeningSummaryProps {
  sessionId: string;
  transcript: string;
}

export const AIScreeningSummary: React.FC<AIScreeningSummaryProps> = ({ 
  sessionId, 
  transcript 
}) => {
  const [analysis, setAnalysis] = useState<ScreeningAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (transcript && transcript.trim()) {
      loadOrGenerateAnalysis();
    }
  }, [transcript, sessionId]);

  const loadOrGenerateAnalysis = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // First try to load existing summary from database
      const { data: existingSummary, error: loadError } = await supabase
        .from('voice_screening_sessions')
        .select('ai_summary, ai_summary_generated_at')
        .eq('id', sessionId)
        .single();

      if (!loadError && existingSummary?.ai_summary) {
        console.log('Loading existing AI summary from database');
        setAnalysis(existingSummary.ai_summary as unknown as ScreeningAnalysis);
        setIsLoading(false);
        return;
      }

      // Also check call_recordings table
      const { data: callRecordingSummary, error: callError } = await supabase
        .from('call_recordings')
        .select('ai_summary, ai_summary_generated_at')
        .eq('id', sessionId)
        .single();

      if (!callError && callRecordingSummary?.ai_summary) {
        console.log('Loading existing AI summary from call recordings');
        setAnalysis(callRecordingSummary.ai_summary as unknown as ScreeningAnalysis);
        setIsLoading(false);
        return;
      }

      // If no existing summary found, generate new one
      await generateAnalysis();
    } catch (error) {
      console.error('Error loading existing analysis:', error);
      await generateAnalysis();
    }
  };

  const generateAnalysis = async () => {
    if (!isLoading) setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('analyze-screening', {
        body: {
          transcript,
          sessionId,
          userId: 'current-user' // This would be auth.uid() in a real implementation
        }
      });

      if (error) throw error;

      if (data?.analysis) {
        setAnalysis(data.analysis);
        
        // Save the analysis to database for future use
        await saveAnalysisToDatabase(data.analysis);
      } else {
        throw new Error('No analysis data received');
      }
    } catch (error) {
      console.error('Error generating analysis:', error);
      setError('Failed to generate AI analysis. Please try again.');
      toast.error('Failed to generate screening analysis');
    } finally {
      setIsLoading(false);
    }
  };

  const saveAnalysisToDatabase = async (analysisData: ScreeningAnalysis) => {
    try {
      // Try to save to voice_screening_sessions first
      const { error: voiceError } = await supabase
        .from('voice_screening_sessions')
        .update({
          ai_summary: analysisData as any,
          ai_summary_generated_at: new Date().toISOString()
        })
        .eq('id', sessionId);

      if (voiceError) {
        // If that fails, try call_recordings
        const { error: callError } = await supabase
          .from('call_recordings')
          .update({
            ai_summary: analysisData as any,
            ai_summary_generated_at: new Date().toISOString()
          })
          .eq('id', sessionId);

        if (callError) {
          console.error('Failed to save AI summary to database:', callError);
        } else {
          console.log('AI summary saved to call_recordings successfully');
        }
      } else {
        console.log('AI summary saved to voice_screening_sessions successfully');
      }
    } catch (error) {
      console.error('Error saving analysis to database:', error);
    }
  };

  const getQualityColor = (quality: string) => {
    switch (quality.toLowerCase()) {
      case 'excellent': return 'text-green-600 bg-green-50 border-green-200';
      case 'good': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'average': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'poor': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation.toLowerCase()) {
      case 'strong_match': return 'text-green-600 bg-green-50 border-green-200';
      case 'potential_match': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'weak_match': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'no_match': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (!transcript) {
    return null;
  }

  return (
    <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Brain className="h-6 w-6 text-blue-600" />
            AI Screening Analysis
            {isLoading && <Loader2 className="h-4 w-4 animate-spin ml-2" />}
          </CardTitle>
          <div className="flex items-center gap-2">
            {!isLoading && analysis && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => generateAnalysis()}
                className="text-blue-600 hover:text-blue-800"
              >
                Refresh Analysis
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="space-y-6">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-blue-600" />
                <p className="text-sm text-blue-600">Analyzing screening conversation...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <span className="text-red-700">{error}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => generateAnalysis()}
                className="ml-auto"
              >
                Retry
              </Button>
            </div>
          )}

          {analysis && (
            <>
              {/* Candidate Summary */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="h-5 w-5 text-blue-600" />
                    <h3 className="font-semibold text-blue-900">Candidate Overview</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-600">Name:</span>
                      <p className="text-gray-900">{analysis.candidate_summary.name}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm font-medium text-gray-600">Experience:</span>
                        <Badge variant="secondary" className="mt-1">
                          {analysis.candidate_summary.experience_level}
                        </Badge>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600">Years:</span>
                        <p className="text-gray-900">{analysis.candidate_summary.years_experience}</p>
                      </div>
                    </div>

                    {analysis.candidate_summary.primary_skills.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Key Skills:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {analysis.candidate_summary.primary_skills.map((skill, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-2 text-sm">
                      {analysis.candidate_summary.current_role !== 'Not mentioned' && (
                        <div className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4 text-gray-500" />
                          <span>{analysis.candidate_summary.current_role}</span>
                        </div>
                      )}
                      {analysis.candidate_summary.location_preference !== 'Not discussed' && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-gray-500" />
                          <span>{analysis.candidate_summary.location_preference}</span>
                        </div>
                      )}
                      {analysis.candidate_summary.salary_expectations !== 'Not discussed' && (
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-gray-500" />
                          <span>{analysis.candidate_summary.salary_expectations}</span>
                        </div>
                      )}
                      {analysis.candidate_summary.availability !== 'Not discussed' && (
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-500" />
                          <span>{analysis.candidate_summary.availability}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Assessment */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                    <h3 className="font-semibold text-blue-900">Assessment</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <span className="text-sm font-medium text-gray-600">Communication:</span>
                        <Badge className={`mt-1 ${getQualityColor(analysis.screening_assessment.communication_quality)}`}>
                          {analysis.screening_assessment.communication_quality}
                        </Badge>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600">Technical:</span>
                        <Badge className={`mt-1 ${getQualityColor(analysis.screening_assessment.technical_competency)}`}>
                          {analysis.screening_assessment.technical_competency}
                        </Badge>
                      </div>
                    </div>

                    {analysis.screening_assessment.green_flags.length > 0 && (
                      <div>
                        <div className="flex items-center gap-1 mb-1">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium text-gray-600">Strengths:</span>
                        </div>
                        <ul className="text-sm text-gray-700 space-y-1">
                          {analysis.screening_assessment.green_flags.map((flag, idx) => (
                            <li key={idx} className="flex items-center gap-1">
                              <span className="w-1 h-1 bg-green-500 rounded-full"></span>
                              {flag}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {analysis.screening_assessment.red_flags.length > 0 && (
                      <div>
                        <div className="flex items-center gap-1 mb-1">
                          <AlertTriangle className="h-4 w-4 text-red-600" />
                          <span className="text-sm font-medium text-gray-600">Concerns:</span>
                        </div>
                        <ul className="text-sm text-gray-700 space-y-1">
                          {analysis.screening_assessment.red_flags.map((flag, idx) => (
                            <li key={idx} className="flex items-center gap-1">
                              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                              {flag}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Job Matches */}
              {analysis.job_matches.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Star className="h-5 w-5 text-blue-600" />
                    <h3 className="font-semibold text-blue-900">Job Match Analysis</h3>
                  </div>
                  
                  <div className="space-y-4">
                    {analysis.job_matches.map((match, idx) => (
                      <div key={idx} className="border rounded-lg p-4 bg-white">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900">{match.job_title}</h4>
                          <div className="flex items-center gap-2">
                            <Progress value={match.match_score} className="w-20 h-2" />
                            <span className="text-sm font-medium">{match.match_score}%</span>
                          </div>
                        </div>
                        
                        <Badge className={`mb-2 ${getRecommendationColor(match.recommendation)}`}>
                          {match.recommendation.replace('_', ' ')}
                        </Badge>
                        
                        <p className="text-sm text-gray-700 mb-2">{match.match_rationale}</p>
                        
                        {match.skill_alignment.length > 0 && (
                          <div className="mb-2">
                            <span className="text-xs font-medium text-gray-600">Matching Skills:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {match.skill_alignment.map((skill, skillIdx) => (
                                <Badge key={skillIdx} variant="outline" className="text-xs">
                                  {skill}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {match.concerns.length > 0 && (
                          <div>
                            <span className="text-xs font-medium text-gray-600">Areas of Concern:</span>
                            <ul className="text-xs text-gray-600 mt-1">
                              {match.concerns.map((concern, concernIdx) => (
                                <li key={concernIdx} className="flex items-center gap-1">
                                  <span className="w-1 h-1 bg-yellow-500 rounded-full"></span>
                                  {concern}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Separator />

              {/* Next Steps */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Recommended Next Steps</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {analysis.next_steps.recommended_actions.map((action, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Interview Focus Areas</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {analysis.next_steps.interview_focus_areas.map((area, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        {area}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Overall Impression */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">Overall Assessment</h4>
                <p className="text-sm text-blue-800">{analysis.screening_assessment.overall_impression}</p>
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
};